/**
 * "Altri ticket dell'associato" — prototipo L395-L409.
 *
 * Card accessoria: i dati arrivano da `GET /:id/related` (Task 7), che gli
 * adapter espongono come `api.getRelatedTickets`. Dove l'adapter non la
 * implementa la card non esiste. Il caricamento e pigro: parte solo quando il
 * pannello e davvero visibile (`active`), cosi nel drawer non si paga una
 * chiamata per un pannello mai aperto, e avviene UNA volta per ticket
 * (`loadedFor`): riaprire il cassetto non rifa la chiamata ne azzera la lista.
 *
 * Un errore non si mostra: e informazione di contorno, non deve sporcare il
 * dettaglio di un ticket che si sta lavorando. Si ricade sulla lista vuota.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { S3Badge } from './primitives';
import { statusVisual } from './visual';
import { displayStatus } from '../displayStatus';
import DerivedStatusMark from '../DerivedStatusMark';
import type { OperatorSupportApi, RelatedTicket } from '../../../services/supportApi';

export interface RelatedCardProps {
  ticketId: string;
  api: OperatorSupportApi;
  /** Il pannello e visibile: finche e falso non si chiama la rete. */
  active?: boolean;
  /** Apertura in pagina: se presente sostituisce la navigazione del link. */
  onOpenTicket?: (id: string) => void;
}

const RelatedCard: React.FC<RelatedCardProps> = ({ ticketId, api, active = true, onOpenTicket }) => {
  const headingId = useId();
  const [related, setRelated] = useState<RelatedTicket[] | null>(null);
  /** Ticket per cui la chiamata e gia partita: evita di rifarla a ogni riapertura. */
  const loadedFor = useRef<string | null>(null);

  const load = api.getRelatedTickets;

  useEffect(() => {
    if (!load || !active || loadedFor.current === ticketId) return;
    let alive = true;
    loadedFor.current = ticketId;
    // Cambio di ticket: la lista precedente non vale piu, si riparte da capo.
    setRelated(null);
    load(ticketId)
      .then((r) => { if (alive) setRelated(r || []); })
      .catch(() => { if (alive) setRelated([]); }); // fallimento silenzioso
    return () => { alive = false; }; // risposta in volo di un ticket abbandonato: si scarta
  }, [active, load, ticketId]);

  if (!load || related === null) return null;

  return (
    <section className="s3-card s3-ctx" aria-labelledby={headingId}>
      <h3 id={headingId} className="s3-h3">{"Altri ticket dell'associato"}</h3>

      {related.length === 0 ? (
        <p className="s3-rel__empty">Nessun altro ticket.</p>
      ) : (
        <ul className="s3-rel">
          {related.map((r) => {
            const st = statusVisual(r.status);
            const shown = displayStatus(r);
            const href = api.ticketPath?.(r._id) || '';
            const body = (
              <>
                <span className="s3-meta s3-rel__num">#{r.ticketNumber}</span>
                <span className="s3-ellipsis s3-rel__subj">{r.subject}</span>
                {shown.derived ? <DerivedStatusMark d={shown} size={11} /> : <S3Badge tone={st.tone} small>{st.label}</S3Badge>}
              </>
            );
            return (
              <li key={r._id}>
                {href ? (
                  <Link
                    to={href}
                    className="s3-rel__item"
                    onClick={(e) => {
                      if (!onOpenTicket) return;
                      e.preventDefault();
                      onOpenTicket(r._id);
                    }}
                  >
                    {body}
                  </Link>
                ) : (
                  // Adapter senza `ticketPath`: si mostra la riga, ma un link
                  // verso il nulla (`to=""` ricaricherebbe la pagina corrente) no.
                  <span className="s3-rel__item">{body}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default RelatedCard;
